'use client';

import { KingsyardLogoMark } from '@/components/KingsyardLogo';
import Link from 'next/link';
import { useEffect, useState } from 'react';

export default function FeederCareLogPage() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [currentYear, setCurrentYear] = useState(2026);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastShortcut, setToastShortcut] = useState('Ctrl + D');
  const [logoError, setLogoError] = useState(false);

  useEffect(() => {
    setCurrentYear(new Date().getFullYear());
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    setToastShortcut(isMac ? 'Cmd + D' : 'Ctrl + D');
  }, []);

  const handleBookmark = () => {
    setToastVisible(true);
  };

  const creativeWorkJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name: 'Free Printable Feeder Care Log | Kingsyard',
    url: 'https://www.kingsyard.com/resources/feeder-care-log',
    description:
      'Free printable feeder care log from Kingsyard. Keep every feeder clean, filled and safe with a monthly cleaning checklist and refill log.',
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.kingsyard.com' },
      { '@type': 'ListItem', position: 2, name: 'Resource Hub', item: 'https://www.kingsyard.com/resources' },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'Free Printable Feeder Care Log',
        item: 'https://www.kingsyard.com/resources/feeder-care-log',
      },
    ],
  };

  return (
    <>
      <script
        type='application/ld+json'
        dangerouslySetInnerHTML={{ __html: JSON.stringify(creativeWorkJsonLd) }}
      />
      <script
        type='application/ld+json'
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />

      <link
        rel='preconnect'
        href='https://fonts.googleapis.com'
      />
      <link
        rel='preconnect'
        href='https://fonts.gstatic.com'
        crossOrigin='anonymous'
      />
      <link
        href='https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap'
        rel='stylesheet'
      />

      <style
        dangerouslySetInnerHTML={{
          __html: `
        @page { size: letter; margin: 0; }

        :root {
            --green-dark: #1a5632;
            --green-mid:  #2d7a4a;
            --green-light:#3fa35d;
            --green-pale: #eaf5ee;
            --cream:      #faf8f4;
            --warm-gray:  #6b6560;
            --text:       #2c2a27;
            --line:       #d4d0ca;
            --gold:       #c1892f;
            --gold-pale:  #faf1e0;
            --sidebar-w:  320px;
            --paper-w:    8.5in;
            --paper-h:    11in;
            --shadow-sm:  0 1px 3px rgba(26,86,50,0.06);
            --shadow-md:  0 6px 18px rgba(26,86,50,0.10);
        }

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        em { font-style: normal; }

        html, body {
            font-family: 'Poppins', 'Segoe UI', sans-serif !important;
            color: var(--text);
            background: #e8e4dd;
            background-image: radial-gradient(circle at 1px 1px, rgba(26,86,50,0.05) 1px, transparent 0);
            background-size: 22px 22px;
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
        }

        input, button, select, textarea, table {
            font-family: 'Poppins', 'Segoe UI', sans-serif !important;
        }

        .top-bar {
            display: none;
            position: sticky;
            top: 0;
            z-index: 120;
            height: 56px;
            background: #fff;
            border-bottom: 1px solid var(--line);
            align-items: center;
            justify-content: space-between;
            padding: 0 12px;
            gap: 10px;
        }
        .tb-menu {
            width: 44px; height: 44px;
            display: flex; align-items: center; justify-content: center;
            background: transparent; border: none; border-radius: 8px;
            cursor: pointer; flex-shrink: 0;
        }
        .tb-menu:hover { background: var(--green-pale); }
        .tb-menu svg { width: 22px; height: 22px; fill: var(--green-dark); }
        .tb-logo {
            position: absolute; left: 50%; top: 50%;
            transform: translate(-50%, -50%);
            display: flex; align-items: center;
        }
        .tb-logo svg { height: 32px; width: auto; display: block; }
        .tb-shop {
            display: inline-flex; align-items: center; justify-content: center;
            min-height: 40px; padding: 8px 16px;
            background: #fff; color: var(--green-dark);
            border: 2px solid var(--green-dark); border-radius: 8px;
            font-size: 14px; font-weight: 600;
            text-decoration: none; flex-shrink: 0;
            transition: all 0.2s;
        }
        .tb-shop:hover { background: var(--green-pale); }

        .tpl-switch {
            display: none;
            position: sticky;
            top: 56px;
            z-index: 110;
            background: #fff;
            border-bottom: 1px solid var(--line);
            padding: 8px 12px;
            gap: 8px;
            overflow-x: auto;
            overscroll-behavior-x: contain;
            scrollbar-width: none;
        }
        .tpl-switch::-webkit-scrollbar { display: none; }
        .chip {
            flex-shrink: 0;
            border: 1px solid var(--line);
            background: #fff;
            color: var(--warm-gray);
            font-size: 13px; font-weight: 600;
            padding: 8px 16px; border-radius: 999px;
            cursor: pointer; white-space: nowrap;
            text-decoration: none;
            transition: all 0.2s;
        }
        .chip:hover { border-color: var(--green-mid); color: var(--green-dark); }
        .chip.active {
            background: linear-gradient(135deg, var(--green-mid), var(--green-dark));
            border-color: var(--green-dark);
            color: #fff;
        }

        .page-layout { display: flex; min-height: 100vh; }

        .sidebar {
            width: var(--sidebar-w);
            flex-shrink: 0;
            background: #fff;
            border-right: 1px solid var(--line);
            display: flex; flex-direction: column;
            position: fixed; top: 0; left: 0;
            height: 100vh; overflow-y: auto;
            z-index: 100;
            box-shadow: var(--shadow-md);
        }

        .sb-header {
            background: linear-gradient(135deg, var(--green-dark) 0%, #143f26 100%);
            padding: 24px;
            display: flex; align-items: center; justify-content: space-between;
            position: relative; overflow: hidden;
            border-bottom: 3px solid var(--gold);
        }
        .sb-header::after {
            content: '';
            position: absolute; top: -20px; right: -20px;
            width: 110px; height: 110px;
            background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100' fill='none'%3E%3Cpath d='M20 70 C20 50, 35 30, 50 25 C55 23, 60 25, 58 30 C56 35, 50 38, 48 42 C46 46, 50 48, 55 46 C60 44, 65 38, 70 35 C75 32, 80 35, 78 40 C76 45, 68 50, 62 52 C56 54, 52 58, 55 62 C58 66, 65 68, 72 65 C79 62, 85 58, 90 60 C95 62, 92 70, 85 72 C78 74, 68 70, 60 68 C52 66, 45 70, 40 75 C35 80, 25 78, 20 70Z' stroke='%23ffffff' stroke-width='1.5' opacity='0.12'/%3E%3C/svg%3E");
            background-size: contain; background-repeat: no-repeat;
            pointer-events: none;
        }
        .sb-header .brand { font-family: 'Poppins', 'Segoe UI', sans-serif !important; color: #fff; font-size: 20px; font-weight: 700; letter-spacing: 2.5px; position: relative; }
        .sb-header .sub { font-family: 'Poppins', 'Segoe UI', sans-serif !important; color: rgba(255,255,255,0.62); font-size: 16px; letter-spacing: 1px; margin-top: 2px; position: relative; }

        .sb-section { padding: 20px 24px; border-bottom: 1px solid #f0ede8; }
        .sb-section:last-child { border-bottom: none; }

        .sb-title {
            font-family: 'Poppins', 'Segoe UI', sans-serif !important;
            font-size: 16px; font-weight: 700; color: var(--green-dark);
            letter-spacing: 1px; margin-bottom: 14px;
            padding-bottom: 8px; border-bottom: 2px solid var(--green-mid);
            display: flex; align-items: center; gap: 8px;
        }
        .sb-title::before {
            content: ''; width: 7px; height: 7px; border-radius: 50%;
            background: var(--gold); flex-shrink: 0;
        }

        .tpl-list { display: flex; flex-direction: column; gap: 6px; }
        .tpl-item {
            display: flex; align-items: center; gap: 10px;
            width: 100%; padding: 11px 12px;
            background: transparent; border: none; border-radius: 8px;
            font-family: 'Poppins', 'Segoe UI', sans-serif !important;
            font-size: 14px; font-weight: 600;
            color: var(--text); text-align: left; cursor: pointer;
            text-decoration: none; transition: all 0.15s;
        }
        .tpl-item:hover { background: var(--green-pale); color: var(--green-dark); }
        .tpl-item.active { background: #d3e8dc; color: var(--green-dark); font-weight: 700; }
        .tpl-item svg {
            width: 18px; height: 18px; flex-shrink: 0;
            stroke: var(--green-mid); fill: none; stroke-width: 1.8;
            stroke-linecap: round; stroke-linejoin: round;
        }
        .tpl-item-name { flex: 1; min-width: 0; }
        .tpl-item-meta { font-family: 'Poppins', 'Segoe UI', sans-serif !important; font-size: 12px; font-weight: 500; color: var(--warm-gray); white-space: nowrap; flex-shrink: 0; }
        .tpl-hint { font-family: 'Poppins', 'Segoe UI', sans-serif !important; margin-top: 12px; font-size: 12px; color: #9a948c; line-height: 1.5; }

        .sb-btn {
            display: flex; align-items: center; justify-content: center; gap: 8px;
            width: 100%; padding: 14px; border: none; border-radius: 8px;
            font-family: 'Poppins', 'Segoe UI', sans-serif !important;
            font-size: 16px; font-weight: 600; cursor: pointer;
            letter-spacing: 0.5px; transition: all 0.2s; white-space: normal;
        }
        .sb-btn svg { width: 18px; height: 18px; transition: transform 0.2s; flex-shrink: 0; }
        .sb-print {
            background: linear-gradient(135deg, var(--green-mid), var(--green-dark));
            color: #fff; box-shadow: 0 3px 10px rgba(26,86,50,0.28);
        }
        .sb-print:hover { box-shadow: 0 5px 14px rgba(26,86,50,0.36); transform: translateY(-1px); }
        .sb-print svg { fill: #fff; }
        .sb-bookmark { background: transparent; color: var(--green-dark); border: 2px solid var(--green-dark); margin-top: 10px; }
        .sb-bookmark:hover { background: var(--green-pale); transform: translateY(-1px); }
        .sb-bookmark svg { fill: var(--green-dark); }

        .sb-suggest {
            display: none; padding: 16px 24px; text-align: center; border-bottom: 1px solid #f0ede8;
        }
        .sb-suggest .suggest-link { font-family: 'Poppins', 'Segoe UI', sans-serif !important; color: var(--green-mid); font-size: 14px; font-weight: 500; text-decoration: underline; }

        .sb-footer { margin-top: auto; padding: 16px 24px; text-align: center; font-size: 16px; color: #aaa; }
        .sb-footer a { font-family: 'Poppins', 'Segoe UI', sans-serif !important; color: var(--green-mid); text-decoration: none; font-weight: 500; }
        .sb-footer a:hover { text-decoration: underline; }

        .sb-overlay {
            display: none; position: fixed; inset: 0; background: rgba(0,0,0,0.4); z-index: 95;
        }
        .sb-overlay.show { display: block; }

        .main-content {
            flex: 1; margin-left: var(--sidebar-w); padding: 24px 32px;
            display: flex; flex-direction: column; align-items: center; min-width: 0; font-size: 16px;
        }
        .main-content * { font-family: 'Poppins', 'Segoe UI', sans-serif !important; }

        .spoke-content {
            width: 100%; max-width: var(--paper-w); background: #fff; border: 1px solid var(--line);
            border-radius: 12px; box-shadow: var(--shadow-sm); padding: 22px 24px; margin-bottom: 20px;
        }
        .breadcrumb { font-family: 'Poppins', 'Segoe UI', sans-serif !important; font-size: 13px; color: var(--warm-gray); margin-bottom: 12px; }
        .breadcrumb a { color: var(--warm-gray); text-decoration: none; }
        .breadcrumb a:hover { color: var(--green-mid); text-decoration: underline; }
        .breadcrumb .sep { margin: 0 8px; color: var(--line); }
        .breadcrumb .current { color: var(--green-dark); font-weight: 600; }
        .spoke-content h1 { font-family: 'Poppins', 'Segoe UI', sans-serif !important; font-size: 24px; font-weight: 700; color: var(--green-dark); margin-bottom: 10px; line-height: 1.3; }
        .spoke-content p { font-family: 'Poppins', 'Segoe UI', sans-serif !important; font-size: 15px; line-height: 1.75; color: var(--warm-gray); margin-bottom: 12px; }
        .tips-title { font-family: 'Poppins', 'Segoe UI', sans-serif !important; font-size: 15px; font-weight: 700; color: var(--green-dark); margin: 14px 0 8px; }
        
        /* 强制显式恢复列表项圆点与缩进样式 */
        .spoke-content .tips { margin: 0 0 12px 0; padding-left: 20px; list-style-type: disc !important; }
        .spoke-content .tips li { font-family: 'Poppins', 'Segoe UI', sans-serif !important; font-size: 15px; line-height: 1.75; color: var(--warm-gray); margin-bottom: 6px; display: list-item !important; list-style-type: disc !important; }

        .tool-bar {
            width: 100%; max-width: var(--paper-w); background: #fff; border: 1px solid var(--line);
            border-radius: 12px; box-shadow: var(--shadow-sm); padding: 18px 22px; margin-bottom: 20px;
            display: flex; align-items: center; justify-content: space-between; gap: 18px;
        }
        @media (min-width: 1200px) {
            .tool-bar { position: sticky; top: 0; z-index: 50; }
        }
        .tb-info { min-width: 0; }
        .tpl-meta { display: flex; flex-wrap: wrap; gap: 8px; }
        .meta-chip {
            font-family: 'Poppins', 'Segoe UI', sans-serif !important;
            display: inline-block; background: var(--green-pale); color: var(--green-dark);
            font-size: 12px; font-weight: 600; letter-spacing: 1px; text-transform: uppercase;
            padding: 4px 10px; border-radius: 999px;
        }
        .tb-actions { display: flex; align-items: center; gap: 10px; flex-shrink: 0; }
        .tb-print-btn, .tb-help-btn {
            display: inline-flex; align-items: center; justify-content: center; gap: 8px;
            font-family: 'Poppins', 'Segoe UI', sans-serif !important;
            font-size: 15px; font-weight: 600; padding: 11px 18px;
            border-radius: 8px; cursor: pointer; transition: all 0.2s; white-space: normal;
        }
        .tb-print-btn {
            background: linear-gradient(135deg, var(--green-mid), var(--green-dark)); color: #fff; border: none;
            box-shadow: 0 3px 10px rgba(26,86,50,0.28);
        }
        .tb-print-btn:hover { box-shadow: 0 5px 14px rgba(26,86,50,0.36); transform: translateY(-1px); }
        .tb-print-btn svg { width: 18px; height: 18px; fill: #fff; flex-shrink: 0; }
        .tb-help-btn { background: transparent; color: var(--green-dark); border: 2px solid var(--green-dark); }
        .tb-help-btn:hover { background: var(--green-pale); transform: translateY(-1px); }
        .tb-help-btn svg { width: 17px; height: 17px; fill: var(--green-dark); flex-shrink: 0; }

        .help-overlay {
            position: fixed; inset: 0; background: rgba(0,0,0,0.4); z-index: 200;
            opacity: 0; transition: opacity 0.25s ease; pointer-events: none;
        }
        .help-overlay.show { opacity: 1; pointer-events: auto; }
        .help-panel {
            position: fixed; top: 0; right: 0; width: 320px; height: 100vh; background: #fff;
            box-shadow: -8px 0 30px rgba(0,0,0,0.18); z-index: 210;
            display: flex; flex-direction: column; transform: translateX(100%); transition: transform 0.3s ease;
        }
        .help-panel.open { transform: translateX(0); }
        .help-hdr {
            display: flex; align-items: center; justify-content: space-between; padding: 18px 20px;
            background: var(--green-pale); border-bottom: 2px solid var(--green-mid); flex-shrink: 0;
        }
        .help-title {
            font-family: 'Poppins', 'Segoe UI', sans-serif !important;
            font-size: 17px; font-weight: 700; color: var(--green-dark); letter-spacing: 0.5px;
            display: flex; align-items: center; gap: 8px;
        }
        .help-title::before { content: ''; width: 7px; height: 7px; border-radius: 50%; background: var(--gold); flex-shrink: 0; }
        .help-close {
            width: 32px; height: 32px; border: none; border-radius: 50%; background: #fff; color: var(--warm-gray);
            font-size: 20px; line-height: 32px; text-align: center; cursor: pointer; transition: all 0.2s; padding: 0; flex-shrink: 0;
        }
        .help-close:hover { background: var(--line); color: var(--text); }
        .help-body { padding: 20px; overflow-y: auto; flex: 1; }
        .method-block { margin-bottom: 14px; padding: 12px 14px; background: var(--green-pale); border-radius: 8px; }
        .method-block:last-child { margin-bottom: 0; }
        .method-label { font-family: 'Poppins', 'Segoe UI', sans-serif !important; font-size: 16px; font-weight: 600; color: var(--green-dark); margin-bottom: 5px; }
        .method-desc { font-family: 'Poppins', 'Segoe UI', sans-serif !important; font-size: 16px; color: #6f6a63; line-height: 1.7; }

        .paper-stack { width: 100%; display: flex; flex-direction: column; align-items: center; }
        .tpl { display: flex; flex-direction: column; align-items: center; width: 100%; }

        .page {
            width: 100%; max-width: var(--paper-w); height: var(--paper-h);
            margin: 0 auto 32px; background: #fff; position: relative; overflow: hidden;
            box-shadow: 0 10px 30px rgba(26,86,50,0.12), 0 2px 8px rgba(0,0,0,0.06);
            display: flex; flex-direction: column;
        }
        .page:last-child { margin-bottom: 0; }
        .page::after {
            content: ''; position: absolute; bottom: 40px; right: 30px; width: 80px; height: 80px;
            background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100' fill='none'%3E%3Cpath d='M20 70 C20 50, 35 30, 50 25 C55 23, 60 25, 58 30 C56 35, 50 38, 48 42 C46 46, 50 48, 55 46 C60 44, 65 38, 70 35 C75 32, 80 35, 78 40 C76 45, 68 50, 62 52 C56 54, 52 58, 55 62 C58 66, 65 68, 72 65 C79 62, 85 58, 90 60 C95 62, 92 70, 85 72 C78 74, 68 70, 60 68 C52 66, 45 70, 40 75 C35 80, 25 78, 20 70Z' stroke='%232d7a4a' stroke-width='1.5' opacity='0.06'/%3E%3C/svg%3E");
            background-size: contain; background-repeat: no-repeat; pointer-events: none; z-index: 0;
        }
        .deco-feather { position: absolute; top: 80px; right: 25px; width: 54px; height: 110px; opacity: 0.05; pointer-events: none; z-index: 0; }

        .page-hdr {
            flex: 0 0 62px; background: #fff; display: flex; align-items: center; justify-content: flex-start;
            padding: 0 40px; position: relative; border-bottom: 3px solid var(--green-mid);
        }
        .page-hdr::after {
            content: ''; position: absolute; left: 0; right: 0; bottom: -3px; height: 3px;
            background: linear-gradient(90deg, var(--gold) 0%, var(--green-mid) 22%, var(--green-mid) 100%);
        }
        .hdr-logo { height: 28px; width: auto; display: block; }
        .hdr-logo-text { font-family: 'Poppins', 'Segoe UI', sans-serif !important; font-size: 20px; font-weight: 800; letter-spacing: 2.5px; color: var(--green-dark); }
        .hdr-title { font-family: 'Poppins', 'Segoe UI', sans-serif !important; position: absolute; right: 40px; font-size: 14px; font-weight: 600; color: var(--green-mid); letter-spacing: 1.5px; text-transform: uppercase; }

        .page-ftr { flex: 0 0 30px; background: linear-gradient(90deg, var(--green-dark), #143f26); display: flex; align-items: center; justify-content: center; }
        .page-ftr span { font-family: 'Poppins', 'Segoe UI', sans-serif !important; color: rgba(255,255,255,0.62); font-size: 14px; letter-spacing: 0.5px; text-align: center; white-space: nowrap; }
        .page-ftr a { font-family: 'Poppins', 'Segoe UI', sans-serif !important; color: rgba(255,255,255,0.62); text-decoration: none; }
        .page-ftr a:hover { text-decoration: underline; }

        .log-body {
            flex: 1; padding: 16px 40px 20px; display: flex; flex-direction: column;
            background: var(--cream); overflow: hidden; position: relative; z-index: 1;
        }

        .meta-bar {
            display: flex; gap: 14px; background: #fff; border: 1px solid var(--line); border-radius: 8px;
            padding: 10px 14px; margin-bottom: 10px; box-shadow: var(--shadow-sm);
        }
        .meta-f { flex: 1; position: relative; min-width: 0; }
        .meta-f:not(:last-child)::after {
            content: ''; position: absolute; right: -7px; top: 2px; bottom: 2px; width: 1px; background: var(--line);
        }
        .meta-f .lbl {
            font-family: 'Poppins', 'Segoe UI', sans-serif !important;
            display: block; font-size: 14px; font-weight: 600; color: var(--green-mid); text-transform: uppercase;
            letter-spacing: 1px; margin-bottom: 6px;
        }
        .meta-f input {
            width: 100%; border: none; border-bottom: 1px solid var(--line); padding: 4px 0;
            font-family: 'Poppins', 'Segoe UI', sans-serif !important;
            font-size: 14px; color: var(--text); background: transparent; outline: none;
            transition: border-color 0.15s;
        }
        .meta-f input:focus { border-bottom-color: var(--green-mid); }

        .sec-title {
            font-family: 'Poppins', 'Segoe UI', sans-serif !important;
            font-size: 14px; font-weight: 700; color: var(--green-dark);
            margin-bottom: 8px; padding: 0 0 4px 12px;
            border-bottom: 2px solid var(--green-mid); display: inline-block;
            position: relative; letter-spacing: 0.3px;
        }
        .sec-title::before {
            content: ''; position: absolute; left: 0; top: 3px;
            width: 6px; height: 6px; border-radius: 50%; background: var(--gold);
        }

        .obs-tbl {
            width: 100%; border-collapse: collapse; margin-bottom: 16px; font-size: 14px;
            border-radius: 8px; overflow: hidden; box-shadow: var(--shadow-sm);
        }
        .obs-tbl thead th {
            font-family: 'Poppins', 'Segoe UI', sans-serif !important;
            background: linear-gradient(135deg, var(--green-mid), var(--green-dark)); color: #fff;
            font-size: 14px; font-weight: 600; text-transform: uppercase;
            letter-spacing: 0.5px; padding: 10px 10px; text-align: left;
        }
        .obs-tbl tbody td {
            border: 1px solid #eae7e1; padding: 8px 10px; height: 86px;
            vertical-align: top; background: #fff; font-size: 14px;
        }
        .obs-tbl tbody tr:nth-child(even) td { background: var(--cream); }
        .obs-tbl.log-tbl tbody td { height: 44px; }
        .obs-tbl.log-tbl .hint-row td { height: auto; }
        .obs-tbl .hint-row td {
            height: auto; padding: 4px 10px; font-size: 13px;
            color: #999; background: var(--gold-pale) !important;
            border-bottom: 2px solid #e6d3ab;
        }
        .obs-tbl td[contenteditable]:focus {
            outline: 2px solid var(--green-mid); outline-offset: -2px; background: #fafffe;
        }

        .mini-ck {
            font-family: 'Poppins', 'Segoe UI', sans-serif !important;
            display: inline-flex; margin-right: 4px; align-items: center; gap: 3px;
            font-size: 12px; font-weight: 500; color: var(--warm-gray); padding: 3px 0;
        }
        .mini-ck input {
            width: 13px; height: 13px; flex-shrink: 0; accent-color: var(--green-mid); cursor: pointer;
        }

        .notes-area {
            flex: 1; min-height: 30px; background: #fff;
            border: 1px solid var(--line); border-radius: 8px;
            padding: 10px 14px; box-shadow: var(--shadow-sm);
            background-image: repeating-linear-gradient(#fff 0px, #fff 27px, #eee9e0 28px);
        }
        .notes-area:focus { outline: 2px solid var(--green-mid); outline-offset: -2px; }
        .notes-area:empty::before { content: attr(data-placeholder); color: #bbb; }

        .ck-intro {
            font-family: 'Poppins', 'Segoe UI', sans-serif !important;
            font-size: 14px; color: var(--warm-gray); margin-bottom: 18px;
            padding: 8px 14px; background: var(--green-pale); border-radius: 6px;
            display: inline-block;
        }
        .ck-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 24px; margin-bottom: 12px; }
        .ck-row {
            display: flex; align-items: center; 
            font-family: 'Poppins', 'Segoe UI', sans-serif !important;
            font-size: 15px; font-weight: 500;
            padding: 5px 8px; border-radius: 5px; cursor: pointer; border: 1px solid transparent;
            transition: background 0.15s, border-color 0.15s; color: #444;
        }
        .ck-row:hover { background: var(--green-pale); color: var(--green-dark); border-color: #d7ecdf; }
        .ck-row input[type="checkbox"] {
            margin-right: 10px; width: 18px; height: 18px; accent-color: var(--green-mid); cursor: pointer; flex-shrink: 0;
        }
        .ck-row:has(input:checked) { background: var(--green-pale); color: var(--green-dark); font-weight: 600; }

        .site-footer {
            background: #0d2410; color: rgba(255,255,255,0.7); padding: 24px 32px;
            display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between;
            gap: 12px; margin-left: var(--sidebar-w);
        }
        .site-footer .footer-copyright { font-family: 'Poppins', 'Segoe UI', sans-serif !important; font-size: 16px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.08em; color: rgba(255,255,255,0.5); }
        .site-footer .footer-links { display: flex; list-style: none; gap: 20px; margin: 0; padding: 0; }
        .site-footer .footer-links a { font-family: 'Poppins', 'Segoe UI', sans-serif !important; font-size: 12px; font-weight: 500; text-transform: uppercase; letter-spacing: 0.06em; color: rgba(255,255,255,0.4); text-decoration: none; transition: color 0.2s; }
        .site-footer .footer-links a:hover { color: #A8D5AA; }

        .toast-overlay {
            position: fixed; inset: 0; background: rgba(0,0,0,0.25); z-index: 998;
            opacity: 0; transition: opacity 0.3s ease; pointer-events: none;
        }
        .toast-overlay.show { opacity: 1; pointer-events: auto; }
        .toast {
            position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%) scale(0.95);
            background: #fff; color: var(--text); padding: 20px 48px 20px 24px; border-radius: 12px;
            font-family: 'Poppins', 'Segoe UI', sans-serif !important;
            font-size: 16px; font-weight: 500; box-shadow: 0 8px 32px rgba(0,0,0,0.18);
            z-index: 999; opacity: 0; transition: all 0.3s ease; pointer-events: none; white-space: nowrap;
        }
        .toast.show { opacity: 1; transform: translate(-50%, -50%) scale(1); pointer-events: auto; }
        .toast kbd {
            display: inline-block; background: var(--green-pale); border: 1px solid var(--line);
            border-radius: 4px; padding: 2px 7px; font-size: 15px; font-weight: 600; color: var(--green-dark); margin: 0 2px;
        }
        .toast-close {
            position: absolute; top: 8px; right: 8px; width: 22px; height: 22px; border-radius: 50%;
            border: none; background: #f0f0f0; color: #999; font-size: 14px; line-height: 22px; text-align: center;
            cursor: pointer; transition: background 0.2s; padding: 0;
        }
        .toast-close:hover { background: #e0e0e0; color: #666; }

        @media (max-width: 1199px) {
            .top-bar { display: flex; }
            .tpl-switch { display: flex; }
            .sidebar {
                transform: translateX(-100%); transition: transform 0.3s ease;
                box-shadow: 4px 0 20px rgba(0,0,0,0.15); width: 300px; top: 56px; height: calc(100vh - 56px); z-index: 130;
            }
            .sidebar .sb-header { display: none; }
            .sidebar.open { transform: translateX(0); }
            .main-content { margin-left: 0; padding: 20px 24px; }
            .site-footer { margin-left: 0; }
            .sb-suggest { display: block; }
        }
        @media (max-width: 768px) {
            .main-content { padding: 16px 12px; }
            .page { margin-bottom: 16px; height: auto; min-height: var(--paper-h); }
            .page-hdr { padding: 0 20px; height: 48px; }
            .hdr-logo { height: 24px; }
            .hdr-title { right: 20px; font-size: 12px; letter-spacing: 1px; }
            .page-ftr span { font-size: 11px; }
            .log-body { padding: 12px 16px 14px; }
            .meta-bar { flex-direction: column; gap: 10px; padding: 10px 12px; }
            .meta-f:not(:last-child)::after { display: none; }
            .tool-bar { flex-direction: column; align-items: stretch; padding: 16px; }
            .tb-actions { flex-direction: column; width: 100%; }
            .tb-print-btn, .tb-help-btn { width: 100%; }
            .site-footer { flex-direction: column; text-align: center; padding: 24px 16px; gap: 16px; }
            .site-footer .footer-links { flex-wrap: wrap; justify-content: center; gap: 12px 16px; }
            .toast { white-space: normal; width: calc(100% - 48px); }
        }

        @media print {
            .sidebar, .top-bar, .tpl-switch, .tool-bar, .sb-overlay, .site-footer,
            .toast, .toast-overlay, .help-panel, .help-overlay, .spoke-content { display: none !important; }
            .main-content { margin-left: 0 !important; padding: 0 !important; }
            .page-layout { display: block !important; }
            .page {
                box-shadow: none !important; margin: 0 !important; width: 8.5in !important;
                max-width: none !important; min-height: 0 !important; height: 11in !important;
                overflow: hidden !important; break-inside: avoid; break-after: page;
            }
            .page:last-child { break-after: auto; }
            body { background: #fff !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `,
        }}
      />

      <div className='top-bar'>
        <button
          className='tb-menu'
          onClick={() => setSidebarOpen(true)}
          aria-label='Open menu'
        >
          <svg viewBox='0 0 24 24'>
            <path d='M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z' />
          </svg>
        </button>
        <span className='tb-logo'>
          <KingsyardLogoMark />
        </span>
        <Link
          className='tb-shop'
          href='https://www.kingsyard.com/bird-feeders'
          target='_blank'
          rel='noopener noreferrer'
        >
          Shop
        </Link>
      </div>

      <nav
        className='tpl-switch'
        aria-label='Template pages'
      >
        <Link
          className='chip'
          href='/resources/birding-journal'
        >
          Birding Journal
        </Link>
        <Link
          className='chip'
          href='/resources/backyard-bird-bingo'
        >
          Backyard Bird Bingo
        </Link>
        <Link
          className='chip active'
          href='/resources/feeder-care-log'
          aria-current='true'
        >
          Feeder Care Log
        </Link>
      </nav>

      <div className='page-layout'>
        <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
          <div className='sb-header'>
            <div>
              <div className='brand'>KINGSYARD</div>
              <div className='sub'>Printable Resources</div>
            </div>
          </div>

          <div className='sb-section'>
            <div className='sb-title'>Templates</div>
            <nav
              className='tpl-list'
              aria-label='Template library'
            >
              <Link
                className='tpl-item'
                href='/resources'
              >
                <svg viewBox='0 0 24 24'>
                  <rect
                    x='4'
                    y='4'
                    width='16'
                    height='16'
                    rx='2'
                  />
                  <line
                    x1='4'
                    y1='12'
                    x2='20'
                    y2='12'
                  />
                  <line
                    x1='12'
                    y1='4'
                    x2='12'
                    y2='20'
                  />
                </svg>
                <span className='tpl-item-name'>All Templates</span>
              </Link>
              <Link
                className='tpl-item'
                href='/resources/birding-journal'
              >
                <svg viewBox='0 0 24 24'>
                  <rect
                    x='5'
                    y='3'
                    width='14'
                    height='18'
                    rx='2'
                  />
                  <line
                    x1='9'
                    y1='8'
                    x2='15'
                    y2='8'
                  />
                  <line
                    x1='9'
                    y1='12'
                    x2='15'
                    y2='12'
                  />
                  <line
                    x1='9'
                    y1='16'
                    x2='13'
                    y2='16'
                  />
                </svg>
                <span className='tpl-item-name'>Birding Journal</span>
                <span className='tpl-item-meta'>3 pages</span>
              </Link>
              <Link
                className='tpl-item'
                href='/resources/backyard-bird-bingo'
              >
                <svg viewBox='0 0 24 24'>
                  <rect
                    x='4'
                    y='4'
                    width='16'
                    height='16'
                    rx='2'
                  />
                  <line
                    x1='4'
                    y1='9.33'
                    x2='20'
                    y2='9.33'
                  />
                  <line
                    x1='4'
                    y1='14.66'
                    x2='20'
                    y2='14.66'
                  />
                  <line
                    x1='9.33'
                    y1='4'
                    x2='9.33'
                    y2='20'
                  />
                  <line
                    x1='14.66'
                    y1='4'
                    x2='14.66'
                    y2='20'
                  />
                </svg>
                <span className='tpl-item-name'>Backyard Bird Bingo</span>
                <span className='tpl-item-meta'>3 pages</span>
              </Link>
              <Link
                className='tpl-item active'
                href='/resources/feeder-care-log'
                aria-current='true'
              >
                <svg viewBox='0 0 24 24'>
                  <path d='M12 3s6 6.5 6 10a6 6 0 0 1-12 0c0-3.5 6-10 6-10z' />
                </svg>
                <span className='tpl-item-name'>Feeder Care Log</span>
                <span className='tpl-item-meta'>1 page</span>
              </Link>
            </nav>
            <p className='tpl-hint'>New templates are added every season.</p>
          </div>

          <div
            className='sb-section'
            style={{ paddingBottom: 24 }}
          >
            <button
              className='sb-btn sb-print'
              onClick={() => window.print()}
            >
              <svg viewBox='0 0 24 24'>
                <path d='M19 8H5c-1.66 0-3 1.34-3 3v6h4v4h12v-4h4v-6c0-1.66-1.34-3-3-3zm-3 11H8v-5h8v5zm3-7c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm-1-9H6v4h12V3z' />
              </svg>
              Print This Page
            </button>
            <button
              className='sb-btn sb-bookmark'
              onClick={handleBookmark}
            >
              <svg viewBox='0 0 24 24'>
                <path d='M17 3H7c-1.1 0-2 .9-2 2v16l7-3 7 3V5c0-1.1-.9-2-2-2z' />
              </svg>
              Bookmark This Page
            </button>
          </div>

          <div className='sb-suggest'>
            <a
              className='suggest-link'
              href='mailto:info@kingsyard.com'
            >
              Suggest an edit
            </a>
          </div>

          <div className='sb-footer'>
            <a href='https://www.kingsyard.com'>www.kingsyard.com</a>
          </div>
        </aside>

        <div
          className={`sb-overlay ${sidebarOpen ? 'show' : ''}`}
          onClick={() => setSidebarOpen(false)}
        ></div>

        <main className='main-content'>
          <div className='spoke-content'>
            <nav
              className='breadcrumb'
              aria-label='Breadcrumb'
            >
              <Link href='https://www.kingsyard.com'>Home</Link>
              <span className='sep'>/</span>
              <Link href='/resources'>Resource Hub</Link>
              <span className='sep'>/</span>
              <span className='current'>Feeder Care Log</span>
            </nav>
            <h1>Free Printable Feeder Care Log</h1>
            <p>
              This free printable feeder care log keeps your cleaning routine on track. A simple checklist covers every
              step, and the monthly log makes it easy to see when each feeder was last cleaned and refilled.
            </p>
            <p>
              Clean feeders keep seed fresh and birds healthy. Print one sheet per month and keep it near your seed
              storage.
            </p>
            <div className='tips-title'>Tips for feeder care</div>
            <ul className='tips'>
              <li>Rinse with one part vinegar and nine parts water, then air dry completely before refilling.</li>
              <li>Check seed for clumping or mold after rain and replace it right away.</li>
              <li>
                Record every refill so you know which feeders empty fastest. Print extra copies of the log for a busy
                month.
              </li>
            </ul>
          </div>

          <div className='tool-bar'>
            <div className='tb-info'>
              <div className='tpl-meta'>
                <span className='meta-chip'>1 page</span>
                <span className='meta-chip'>US Letter</span>
                <span className='meta-chip'>Free to print</span>
              </div>
            </div>
            <div className='tb-actions'>
              <button
                className='tb-help-btn'
                onClick={() => setHelpOpen(true)}
                aria-expanded={helpOpen}
              >
                <svg viewBox='0 0 24 24'>
                  <path d='M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z' />
                </svg>
                How to Use
              </button>
              <button
                className='tb-print-btn'
                onClick={() => window.print()}
              >
                <svg viewBox='0 0 24 24'>
                  <path d='M19 8H5c-1.66 0-3 1.34-3 3v6h4v4h12v-4h4v-6c0-1.66-1.34-3-3-3zm-3 11H8v-5h8v5zm3-7c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm-1-9H6v4h12V3z' />
                </svg>
                Print This Page
              </button>
            </div>
          </div>

          <div className='paper-stack'>
            <section
              className='tpl'
              aria-label='Feeder Care Log template'
            >
              <div className='page'>
                <svg
                  className='deco-feather'
                  style={{ top: 80, right: 25, width: 54, height: 110 }}
                  viewBox='0 0 54 110'
                  fill='none'
                >
                  <path
                    d='M27 8 C27 8, 45 36, 45 54 A18 18 0 0 1 9 54 C9 36, 27 8, 27 8Z'
                    stroke='#2d7a4a'
                    strokeWidth='1'
                    opacity='0.06'
                  />
                  <path
                    d='M18 56 A9 9 0 0 0 30 66'
                    stroke='#2d7a4a'
                    strokeWidth='0.8'
                    opacity='0.05'
                  />
                </svg>
                <div className='page-hdr'>
                  <span className='hdr-logo-text'>
                    <KingsyardLogoMark className='hdr-logo' />
                  </span>
                  <span className='hdr-title'>Feeder Care Log</span>
                </div>
                <div className='log-body'>
                  <div className='meta-bar'>
                    <div className='meta-f'>
                      <span className='lbl'>Month</span>
                      <input type='text' />
                    </div>
                    <div className='meta-f'>
                      <span className='lbl'>Completed By</span>
                      <input type='text' />
                    </div>
                  </div>
                  <span className='sec-title'>Cleaning Checklist</span>
                  <div className='ck-grid'>
                    <label className='ck-row'>
                      <input type='checkbox' /> Scrub feeder with warm water and a brush
                    </label>
                    <label className='ck-row'>
                      <input type='checkbox' /> Rinse with 1 part vinegar, 9 parts water
                    </label>
                    <label className='ck-row'>
                      <input type='checkbox' /> Check seed for mold or clumping
                    </label>
                    <label className='ck-row'>
                      <input type='checkbox' /> Inspect hanger, perches and baffles
                    </label>
                  </div>
                  <p
                    className='ck-intro'
                    style={{ fontSize: 13 }}
                  >
                    Clean tube feeders every 2 weeks and hummingbird feeders weekly, more often in hot weather.
                  </p>
                  <span className='sec-title'>Cleaning and Refill Log</span>
                  <table className='obs-tbl log-tbl'>
                    <thead>
                      <tr>
                        <th style={{ width: '13%' }}>Date</th>
                        <th style={{ width: '21%' }}>Feeder</th>
                        <th style={{ width: '32%' }}>Task</th>
                        <th style={{ width: '16%' }}>Seed Type</th>
                        <th style={{ width: '18%' }}>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className='hint-row'>
                        <td>e.g. Mar 3</td>
                        <td>Tube feeder</td>
                        <td>e.g. Clean</td>
                        <td>Sunflower</td>
                        <td>Soaked and air dried</td>
                      </tr>
                      {[...Array(7)].map((_, i) => (
                        <tr key={i}>
                          <td contentEditable='true'></td>
                          <td contentEditable='true'></td>
                          <td>
                            <label className='mini-ck'>
                              <input type='checkbox' /> Clean
                            </label>
                            <label className='mini-ck'>
                              <input type='checkbox' /> Refill
                            </label>
                            <label className='mini-ck'>
                              <input type='checkbox' /> Repair
                            </label>
                          </td>
                          <td contentEditable='true'></td>
                          <td contentEditable='true'></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <span className='sec-title'>Notes</span>
                  <div
                    className='notes-area'
                    contentEditable='true'
                    data-placeholder='Repairs, replacements and anything worth remembering...'
                  ></div>
                </div>
                <div className='page-ftr'>
                  <span>
                    <Link href='https://www.kingsyard.com'>www.kingsyard.com</Link> &bull; Premium Bird Feeding
                    Solutions
                  </span>
                </div>
              </div>
            </section>
          </div>
        </main>
      </div>

      <footer className='site-footer'>
        <div className='footer-copyright'>
          &copy; <span>{currentYear}</span> Kingsyard Inc.
        </div>
        <ul className='footer-links'>
          <li>
            <Link href='https://www.kingsyard.com/privacy-policy'>Privacy Policy</Link>
          </li>
          <li>
            <Link href='https://www.kingsyard.com/terms-privacy'>Terms of Use</Link>
          </li>
          <li>
            <Link href='https://www.kingsyard.com/contact-us'>Contact Us</Link>
          </li>
          <li>
            <Link href='https://www.kingsyard.com/bird-feeders'>Shop</Link>
          </li>
        </ul>
      </footer>

      <div
        className={`help-overlay ${helpOpen ? 'show' : ''}`}
        onClick={() => setHelpOpen(false)}
      ></div>
      <aside
        className={`help-panel ${helpOpen ? 'open' : ''}`}
        aria-hidden={!helpOpen}
        aria-label='How to Use'
      >
        <div className='help-hdr'>
          <h2 className='help-title'>How to Use</h2>
          <button
            className='help-close'
            onClick={() => setHelpOpen(false)}
            aria-label='Close how to use'
          >
            &times;
          </button>
        </div>
        <div className='help-body'>
          <div className='method-block'>
            <div className='method-label'>Method 1: Fill and Print</div>
            <p className='method-desc'>
              Fill in the template on screen, then click Print This Page to print your completed pages.
            </p>
          </div>
          <div className='method-block'>
            <div className='method-label'>Method 2: Print and Go</div>
            <p className='method-desc'>Print blank pages first, carry them outdoors, and fill them in by hand.</p>
          </div>
        </div>
      </aside>

      <div
        className={`toast-overlay ${toastVisible ? 'show' : ''}`}
        onClick={() => setToastVisible(false)}
      ></div>
      <div className={`toast ${toastVisible ? 'show' : ''}`}>
        <button
          className='toast-close'
          onClick={() => setToastVisible(false)}
        >
          &times;
        </button>
        <span>
          Press <kbd>{toastShortcut}</kbd> to bookmark this page
        </span>
      </div>
    </>
  );
}
